import CloseIcon from '@/components/svgs/Close'
import * as DialogPrimitive from '@/ui/primitives/dialog'
import { cn } from '@/ui/utils'
import * as React from 'react'
import { Platform, Pressable, StyleSheet, View } from 'react-native'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'

import type {
	DialogContentComponent,
	DialogContentContextProps,
	DialogContentRef,
	DialogContentVariant,
	DialogDescriptionComponent,
	DialogDescriptionRef,
	DialogFooterComponent,
	DialogHeaderComponent,
	DialogOverlayComponent,
	DialogOverlayRef,
	DialogTitleComponent,
	DialogTitleRef,
} from './dialog.types'

import Logo from '@/components/svgs/Logo'
import { LogoColorVariant } from '@/components/svgs/Logo'
import { HStack } from '@/ui/core/stack'
import { useScreenDimensions } from '@/ui/hooks/useScreenDimensions'
import { dialogStyles } from './dialog.styles'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

// @ts-ignore
const CloseButtonVariantMap: Record<DialogContentVariant, string> = {
	info: '#006B5C',
	confirmation: '#F8EDDF',
	nav: '#95CFC4',
	billing: '#95CFC4',
}

// @ts-ignore
const LogoVariantMap: Record<DialogContentVariant, LogoColorVariant> = {
	info: LogoColorVariant.DARK,
	confirmation: LogoColorVariant.DARK,
	nav: LogoColorVariant.LIGHT,
	billing: LogoColorVariant.LIGHT,
}

const DialogContentContext = React.createContext<DialogContentContextProps>({
	variant: 'info',
})

const DialogOverlayWeb = React.forwardRef<DialogOverlayRef, DialogOverlayComponent>(
	({ className, ...props }, ref) => {
		const { open } = DialogPrimitive.useRootContext()
		const { overlayWebOpen, overlayWebClosed } = dialogStyles()

		return (
			<DialogPrimitive.Overlay
				style={StyleSheet.absoluteFill}
				className={cn(open ? overlayWebOpen() : overlayWebClosed(), className)}
				{...props}
				ref={ref}
			/>
		)
	}
)

DialogOverlayWeb.displayName = 'DialogOverlayWeb'

const DialogOverlayNative = React.forwardRef<DialogOverlayRef, DialogOverlayComponent>(
	({ className, children, ...props }, ref) => {
		const { overlayNative } = dialogStyles()

		return (
			<DialogPrimitive.Overlay
				style={StyleSheet.absoluteFill}
				className={cn(overlayNative(), className)}
				{...props}
				ref={ref}
			>
				<Animated.View entering={FadeIn.duration(150)} exiting={FadeOut.duration(150)}>
					{/* biome-ignore lint/complexity/noUselessFragments: <explanation> */}
					<>{children}</>
				</Animated.View>
			</DialogPrimitive.Overlay>
		)
	}
)

DialogOverlayNative.displayName = 'DialogOverlayNative'

const DialogOverlay = Platform.select({
	web: DialogOverlayWeb,
	default: DialogOverlayNative,
})

const DialogContent = React.forwardRef<DialogContentRef, DialogContentComponent>(
	(
		{
			className,
			contentClassName,
			children,
			showLogo = true,
			variant = 'info',
			...props
		},
		ref
	) => {
		const { open } = DialogPrimitive.useRootContext()
		const { contentOpen, contentClosed, closeButton, logoContainer } = dialogStyles()
		const { height: screenHeight } = useScreenDimensions()
		const contentHeight = Platform.OS === 'web' ? '100dvh' : screenHeight

		return (
			<DialogPortal>
				<DialogOverlay>
					<DialogContentContext.Provider
						value={{
							variant,
						}}
					>
						<DialogPrimitive.Content
							ref={ref}
							className={cn(
								open ? contentOpen({ variant }) : contentClosed({ variant }),
								className
							)}
							//@ts-ignore
							style={{ height: contentHeight }}
							asChild
							{...props}
						>
							<View className="flex web:flex-1">
								<HStack fullWidth className="h-16 justify-between items-start">
									{showLogo && (
										<View className={cn(logoContainer())}>
											<Logo color={LogoVariantMap[variant]} />
										</View>
									)}
									<DialogPrimitive.Close asChild className={cn(closeButton())}>
										<Pressable>
											<CloseIcon
												height={Platform.OS === 'web' ? 16 : 18}
												width={Platform.OS === 'web' ? 16 : 18}
												color={CloseButtonVariantMap[variant]}
											/>
										</Pressable>
									</DialogPrimitive.Close>
								</HStack>
								<View
									className={cn(
										'flex-1 items-center justify-center w-full',
										contentClassName
									)}
								>
									{children}
								</View>
							</View>
						</DialogPrimitive.Content>
					</DialogContentContext.Provider>
				</DialogOverlay>
			</DialogPortal>
		)
	}
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: DialogHeaderComponent) => {
	const { header } = dialogStyles()

	return <View className={cn(header(), className)} {...props} />
}
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: DialogFooterComponent) => {
	const { footer } = dialogStyles()

	return <View className={cn(footer(), className)} {...props} />
}
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<DialogTitleRef, DialogTitleComponent>(
	({ className, ...props }, ref) => {
		const { title } = dialogStyles()
		const { variant } = React.useContext(DialogContentContext)

		return (
			<DialogPrimitive.Title
				ref={ref}
				className={cn(title({ variant }), className)}
				{...props}
			/>
		)
	}
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
	DialogDescriptionRef,
	DialogDescriptionComponent
>(({ className, ...props }, ref) => {
	const { description } = dialogStyles()
	const { variant } = React.useContext(DialogContentContext)

	return (
		<DialogPrimitive.Description
			ref={ref}
			className={cn(description({ variant }), className)}
			{...props}
		/>
	)
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
